/**
 * Every TanStack Query key the SPA uses, in one place.
 *
 * Keys used to be written inline at each call site, and two of them drifted away from
 * the invalidation meant to refresh them: a kassa edit invalidated `["finance-overview"]`
 * while the finance overview was cached as `["finance","overview"]`, and a stage edit
 * invalidated `["pipeline-stages"]` while the deal screen cached the same call as
 * `["stages"]`. Neither failed loudly — an invalidation that matches no key is a silent
 * no-op — so saving a setting simply left the screen it changed showing the old numbers
 * until the 30s `staleTime` expired.
 *
 * The cure is that a key and its invalidation are now the same expression. Nothing under
 * `src/` writes a key array by hand: a query and the mutation that refreshes it both call
 * a function from here, so the two cannot drift apart again.
 *
 * Where a mutation has to refresh a family rather than a single entry, the group exposes
 * `all()`. TanStack matches by prefix, so `queryKeys.payments.all()` reaches both
 * `payments.list(filter)` and `payments.approvalQueue()`.
 */
import type { Currency } from "./types";

/** The same shape the endpoint layer accepts as query parameters. */
export type QueryParams = Readonly<Record<string, string | number | undefined>>;

export const queryKeys = {
  // ------------------------------------------------------------------- auth
  auth: {
    config: () => ["auth", "config"] as const,
    telegramIdentity: () => ["auth", "telegram-identity"] as const,
  },

  // ---------------------------------------------------------------- profile (spec §5)
  // The signed-in person's own data. Everything under one root, so a sign-out or a profile save
  // can refresh the menu and the page together.
  profile: {
    root: ["profile"] as const,
    preferences: () => ["profile", "preferences"] as const,
    availability: () => ["profile", "availability"] as const,
    leaves: () => ["profile", "leaves"] as const,
    colleagues: () => ["profile", "colleagues"] as const,
    sessions: () => ["profile", "sessions"] as const,
    twoFactor: () => ["profile", "two-factor"] as const,
    myWeek: () => ["profile", "my-week"] as const,
  },
  team: {
    status: () => ["team", "status"] as const,
  },

  // ------------------------------------------------------------------ agency
  // This phase's agency screens: package and credits, the onboarding wizard, the import.
  agency: {
    root: ["agency"] as const,
    billing: () => ["agency", "billing"] as const,
    creditRequests: (page: number) => ["agency", "credit-requests", page] as const,
    onboarding: () => ["agency", "onboarding"] as const,
    importBatch: (id: number) => ["agency", "import", id] as const,
  },

  // ------------------------------------------------------------------ platform
  // The Kapitan panel (platform admins). Every list is keyed by its page and filter.
  platform: {
    /** Every platform query: invalidating this one refreshes the whole panel. */
    root: ["platform"] as const,
    tenant: (id: number) => ["platform", "tenant", id] as const,
    tenants: (page: number) => ["platform", "tenants", page] as const,
    tariffs: (page: number) => ["platform", "tariffs", page] as const,
    demoRequests: (page: number, status: string) =>
      ["platform", "demo-requests", page, status] as const,
    creditRequests: (page: number, status: string) =>
      ["platform", "credit-requests", page, status] as const,
    providerHealth: () => ["platform", "providers"] as const,
    airports: (page: number) => ["platform", "airports", page] as const,
    destinations: (page: number) => ["platform", "destinations", page] as const,
    catalogueProviders: () => ["platform", "catalogues", "providers"] as const,
    catalogueTemplates: () => ["platform", "catalogues", "templates"] as const,
    catalogueHotels: (page: number) => ["platform", "catalogues", "hotels", page] as const,
    audit: (page: number, tenant: number | undefined) =>
      ["platform", "audit", page, tenant ?? null] as const,
    support: (page: number) => ["platform", "support", page] as const,
  },

  // ---------------------------------------------------------------- support
  // The agency's view of an open support session (TZ §2.3). Read by every member.
  support: {
    banner: () => ["support", "banner"] as const,
  },

  // ------------------------------------------------------------------ core
  branches: () => ["branches"] as const,
  dashboard: () => ["dashboard"] as const,

  /** `Bosh sahifa` per role (09 §3, §4). The manager's key carries its filters. */
  home: {
    operator: () => ["home", "operator"] as const,
    manager: (filters: { period: string; branch: number | null }) => ["home", "manager", filters] as const,
  },

  /** A client's recognised identities and the merges that folded records into it. */
  clientLinks: {
    identities: (clientId: number) => ["client-identities", clientId] as const,
    merges: (clientId: number) => ["client-merge-log", clientId] as const,
  },

  /** `Ommaviy xabar`: the remaining allowance of one account. */
  bulk: {
    quota: (accountId: number) => ["bulk", "quota", accountId] as const,
  },

  // ------------------------------------------------------------------ inbox
  // The realtime socket invalidates by these prefixes (see `keysForFrame` in
  // lib/realtime.ts), so a key added here must sit under one of them to be refreshed live.
  inbox: {
    all: () => ["inbox"] as const,
    list: (filters: QueryParams) => ["inbox", "list", filters] as const,
    conversation: (id: number) => ["inbox", "conversation", id] as const,
    thread: (id: number) => ["inbox", "thread", id] as const,
    outbound: (id: number) => ["inbox", "outbound", id] as const,
    /** The "/" phrase library, filled for one thread. */
    quickReplies: (id: number) => ["inbox", "quick-replies", id] as const,
    /** The offers the thread's client may be sent. */
    offers: (id: number) => ["inbox", "offers", id] as const,
    /** The AI's hand-overs and injection flag for one thread. */
    events: (id: number) => ["inbox", "events", id] as const,
    /** The client's messages across every channel. */
    allChannels: (clientId: number) => ["inbox", "client-timeline", clientId] as const,
    /** The chip numbers (09 §7). Refetched on every `conversation.*` frame. */
    counts: (filters: QueryParams) => ["inbox", "counts", filters] as const,
    /** `Murojaatlar`: the free queue. */
    queue: (filters: QueryParams) => ["inbox", "queue", filters] as const,
    /** The thread's right panel. */
    panel: (id: number) => ["inbox", "panel", id] as const,
    /** The deal's document checklist, read when "Hujjat ro'yxati" is pressed. */
    checklist: (id: number) => ["inbox", "checklist", id] as const,
  },
  /** Sozlamalar → Kanallar: distribution per branch, and the Telegram exclusion list. */
  channelSettings: {
    distribution: () => ["channel-settings", "distribution"] as const,
    chats: (accountId: number) => ["channel-settings", "chats", accountId] as const,
  },
  /** The contact pane's client cards (§3.2): the portrait, its suggestions, deals and offers. */
  clientCard: {
    portrait: (clientId: number) => ["inbox", "client-card", "portrait", clientId] as const,
    suggestions: (clientId: number) => ["inbox", "client-card", "suggestions", clientId] as const,
    deals: (clientId: number) => ["inbox", "client-card", "deals", clientId] as const,
    offers: (clientId: number) => ["inbox", "client-card", "offers", clientId] as const,
    feed: (clientId: number, kind: string) => ["client-card", "feed", clientId, kind] as const,
    factSummary: (clientId: number) => ["client-card", "fact-summary", clientId] as const,
  },
  assistantQuestions: (scope: string) => ["assistant", "questions", scope] as const,
  aiDrafts: {
    all: () => ["ai-drafts"] as const,
    pending: () => ["ai-drafts", "pending"] as const,
    /** The pending draft of one thread. Under `ai-drafts`, so the approval frame refreshes it. */
    forConversation: (id: number) => ["ai-drafts", "conversation", id] as const,
    /** Every draft of one thread, decided ones too: the approver line on an AI message. */
    decided: (id: number) => ["ai-drafts", "decided", id] as const,
  },

  nav: {
    /** The sidebar's four numbers. Refetched on realtime events and on an interval. */
    counts: () => ["nav", "counts"] as const,
  },
  approvals: {
    /** Prefix: every approval list, whatever its status. Used after a decision. */
    all: () => ["approvals"] as const,
    pending: () => ["approvals", "list", "pending"] as const,
  },
  notifications: {
    /** Prefix: the badge and the panel both. Used after anything that may notify. */
    all: () => ["notifications"] as const,
    list: () => ["notifications", "list"] as const,
    unreadCount: () => ["notifications", "unread-count"] as const,
    /** `Profil → Bildirishnomalar`: the person's own switches. */
    preferences: () => ["notifications", "preferences"] as const,
  },

  /** `Sozlamalar → Audit jurnali` (TZ §22). Filters are part of the key, so each view caches. */
  auditEvents: {
    all: () => ["audit"] as const,
    list: (filters: QueryParams) => ["audit", "list", filters] as const,
  },

  // -------------------------------------------------------------- instagram
  /** `Kommentlar`. Under `inbox`, so the realtime fallback tick refreshes it too. */
  instagramComments: {
    all: () => ["inbox", "comments"] as const,
    list: (filters: QueryParams) => ["inbox", "comments", "list", filters] as const,
  },
  /** `Kanallar` → Instagram: the connection status. */
  instagram: {
    status: () => ["instagram", "status"] as const,
  },
  /** `Instagram postlari`: the synced posts and their promo cards. */
  instagramPosts: {
    all: () => ["instagram-posts"] as const,
    list: (page: number) => ["instagram-posts", "list", page] as const,
  },

  // -------------------------------------------------------------- settings
  memberships: () => ["memberships"] as const,
  permissionMatrix: () => ["permission-matrix"] as const,
  tenantSettings: () => ["tenant-settings"] as const,
  settingsRegistry: () => ["settings-registry"] as const,
  tenantProfile: () => ["tenant-profile"] as const,
  settingsChannel: (accountId: number) => ["settings-channel", accountId] as const,
  messagingAccounts: () => ["messaging-accounts"] as const,

  telegramAccounts: {
    /** Prefix: the account list, and anything that changes an account's state. */
    all: () => ["telegram-accounts"] as const,
    list: () => ["telegram-accounts", "list"] as const,
  },

  // ------------------------------------------------------------------- crm
  kanban: () => ["kanban"] as const,
  lostReasons: () => ["lost-reasons"] as const,
  /** The pipeline stages. One key for both readers: the board and the settings screen. */
  stages: () => ["stages"] as const,

  deals: {
    /** Prefix: every deal list, whatever its filters. */
    all: () => ["deals"] as const,
    /** The flat list the payment and expense modals pick a deal from. */
    forModal: () => ["deals", "for-modal"] as const,
    list: (filter: QueryParams) => ["deals", "list", filter] as const,
  },

  deal: {
    /** Prefix: every cached single deal, for changes that touch all of them. */
    all: () => ["deal"] as const,
    detail: (dealId: number) => ["deal", dealId] as const,
    notes: (dealId: number) => ["deal-notes", dealId] as const,
    bookings: (dealId: number) => ["deal-bookings", dealId] as const,
    proposals: (dealId: number) => ["deal-proposals", dealId] as const,
    stageHistory: (dealId: number) => ["deal-stage-history", dealId] as const,
  },

  travelerDocuments: {
    /** One traveller's files: a list that is refetched whenever its dialog opens, since links expire. */
    list: (travelerId: number) => ["traveler-documents", travelerId] as const,
  },

  /** The owner's grant list for one traveller. Never cached across travellers. */
  passportGrants: (travelerId: number) => ["traveler-passport-grants", travelerId] as const,

  proposal: {
    /** One offer page with its options. */
    detail: (proposalId: number) => ["proposal", proposalId] as const,
  },

  clients: {
    /** Prefix: the list under every search term. */
    all: () => ["clients"] as const,
    list: (search: string) => ["clients", search] as const,
    /** §8 list header figures; a write that changes a segment invalidates `all()`. */
    overview: () => ["clients", "overview"] as const,
    detail: (clientId: number) => ["client", clientId] as const,
    deals: (clientId: number) => ["client-deals", clientId] as const,
    /** The client card's traveller block — a larger page than the deal picker below. */
    travelers: (clientId: number) => ["client-travelers", clientId] as const,
    /** §3.7's "o'xshash mijozlar" on the card. */
    duplicates: (clientId: number) => ["client-duplicates", clientId] as const,
    /** §8 R7 phone check on the new-client page, by the number as typed. */
    phoneCheck: (phone: string) => ["clients", "phone-check", phone] as const,
    /** §8 R7 "Mos turlar", keyed by the brief fields it is computed from. */
    quickMatch: (query: string) => ["clients", "quick-match", query] as const,
    /** §8 R7 "Suhbat yo'riqnomasi" for an empty form. */
    guide: () => ["clients", "guide"] as const,
    /** §8 bulk Telegram job progress. */
    bulkJob: (jobId: number) => ["clients", "bulk-job", jobId] as const,
  },

  /** The deal screen's traveller picker: the same endpoint, a different page size. */
  travelers: (clientId: number) => ["travelers", clientId] as const,

  // --------------------------------------------------------------- finance
  finance: {
    overview: () => ["finance", "overview"] as const,
  },

  payments: {
    /** Prefix: the filtered list and the approval queue together. */
    all: () => ["payments"] as const,
    list: (filter: string) => ["payments", filter] as const,
    approvalQueue: () => ["payments", "approval-queue"] as const,
  },

  tasks: {
    /** Prefix: every task list, whatever its filter. */
    all: () => ["tasks"] as const,
    list: (filter: QueryParams) => ["tasks", "list", filter] as const,
  },

  expenses: () => ["expenses"] as const,
  expenseCategories: () => ["expense-categories"] as const,
  vendors: () => ["vendors"] as const,
  cashAccounts: () => ["cash-accounts"] as const,

  /** The rate of a given date, never of today — a payment snapshots its own date. */
  rate: (date: string, currency: Currency) => ["rate", date, currency] as const,

  dealSummary: {
    /** Prefix: a payment changes the summary of whichever deal it belongs to. */
    all: () => ["deal-summary"] as const,
    detail: (dealId: number) => ["deal-summary", dealId] as const,
  },

  expectedPayments: {
    all: () => ["expected-payments"] as const,
    open: () => ["expected-payments", "open"] as const,
  },

  discipline: (clientId: number) => ["discipline", clientId] as const,

  reports: {
    dynamics: (query: QueryParams) => ["report", "dynamics", query] as const,
    marginByDestination: (query: QueryParams) =>
      ["report", "margin-destination", query] as const,
    marginByVendor: (query: QueryParams) => ["report", "margin-vendor", query] as const,
    debtors: (query: QueryParams) => ["report", "debtors", query] as const,
    managers: (query: QueryParams) => ["report", "managers", query] as const,
    branches: (query: QueryParams) => ["report", "branches", query] as const,
  },

  /** The Hisobotlar pages, one key per report endpoint and its query. */
  reportPage: (name: string, query: QueryParams) => ["reports-page", name, query] as const,

  // ----------------------------------------------------------- integrations
  integrations: {
    platform: () => ["integrations", "platform"] as const,
    platformHealth: () => ["integrations", "platform-health"] as const,
    agency: () => ["integrations", "agency"] as const,
    agencyHealth: () => ["integrations", "agency-health"] as const,
    providerLogins: () => ["integrations", "provider-logins"] as const,
    /** Every integration query, for a refresh after any save or check. */
    all: () => ["integrations"] as const,
  },

  // ------------------------------------------------------------------ ai agent
  ai: {
    control: () => ["ai", "control"] as const,
    assistantHistory: () => ["ai", "assistant", "history"] as const,
    quality: () => ["ai", "quality"] as const,
    diary: (day: string | undefined) => ["ai", "diary", day ?? "today"] as const,
    versions: () => ["ai", "versions"] as const,
    playgroundScenarios: () => ["ai", "playground", "scenarios"] as const,
    configOptions: () => ["ai", "versions", "options"] as const,
    diff: (id: number) => ["ai", "versions", id, "diff"] as const,
    channels: () => ["ai", "channels"] as const,
    runs: (status: string | undefined) => ["ai", "runs", status ?? "all"] as const,
    /** One run in full, for the journal's detail sheet. */
    run: (id: number) => ["ai", "run", id] as const,
    drafts: () => ["ai", "drafts"] as const,
    knowledge: () => ["ai", "knowledge"] as const,
    knowledgeSearch: (q: string) => ["ai", "knowledge", "search", q] as const,
    knowledgeGaps: () => ["ai", "knowledge", "gaps"] as const,
    tasks: () => ["ai", "tasks"] as const,
    jobs: () => ["ai", "jobs"] as const,
    testCases: () => ["ai", "test-cases"] as const,
    testRuns: () => ["ai", "test-runs"] as const,
    suggestions: () => ["ai", "suggestions"] as const,
    /** Every AI query, for a refresh after any change to the agent. */
    all: () => ["ai"] as const,
  },

  // ----------------------------------------------------------------- tours
  tours: {
    providers: () => ["tours", "providers"] as const,
    claims: (provider: string) => ["tours", "claims", provider] as const,
  },

  // ----------------------------------------------------------- tour search
  /** Fan-out searches: one session per id, polled while a supplier is still running. */
  priceWatches: {
    all: () => ["price-watches"] as const,
    list: () => ["price-watches", "list"] as const,
  },
  halalHotels: {
    all: () => ["halal-hotels"] as const,
    list: () => ["halal-hotels", "list"] as const,
  },
  packages: {
    all: () => ["packages"] as const,
    list: (query: string) => ["packages", "list", query] as const,
  },
  tourSearch: {
    all: () => ["tour-search"] as const,
    list: () => ["tour-search", "list"] as const,
    session: (sessionId: number) => ["tour-search", sessionId] as const,
    offers: (sessionId: number) => ["tour-search", sessionId, "offers"] as const,
    variants: (sessionId: number, currency: string, budget: string) =>
      ["tour-search", sessionId, "variants", currency, budget] as const,
  },

  // ------------------------------------------------------------- proposals
  /** The client's page. Keyed by token: the token is what the link carries. */
  publicProposal: {
    all: () => ["public-proposal"] as const,
    page: (token: string) => ["public-proposal", token] as const,
  },
  dealsForProposal: () => ["deals", "for-proposal"] as const,
  /** The supplier-place review queue. A decision refreshes it and the searches that use it. */
  providerPlaces: {
    all: () => ["provider-places"] as const,
    review: () => ["provider-places", "review"] as const,
  },
  hotelReviews: {
    all: () => ["hotel-reviews"] as const,
    review: () => ["hotel-reviews", "pending"] as const,
  },
  /** Catalog places a search is started from. Shared, so a change is rare. */
  catalogPlaces: {
    destinations: () => ["catalog", "destinations"] as const,
    departureCities: () => ["catalog", "departure-cities"] as const,
  },
  /** The agency's own destination selection (`Sozlamalar → Yo'nalishlar`). */
  agencyDestinations: {
    root: ["catalog", "tenant-destinations"] as const,
    list: (page: number) => ["catalog", "tenant-destinations", page] as const,
  },

  // ------------------------------------------------- contracts and payment links
  contracts: {
    /** Prefix: every contract list and template list. */
    all: () => ["contracts"] as const,
    list: () => ["contracts", "list"] as const,
    templates: () => ["contracts", "templates"] as const,
  },

  paymentLinks: {
    /** Prefix: every page of the payment link list. */
    all: () => ["payment-links"] as const,
    list: (page: number) => ["payment-links", "list", page] as const,
  },

  // ------------------------------------------------------- public and avia
  signing: {
    document: (token: string) => ["signing", token] as const,
  },

  payLink: (token: string) => ["pay-link", token] as const,

  // ------------------------------------------- automations and client AI summary
  automation: {
    rules: () => ["automation", "rules"] as const,
    ruleOptions: () => ["automation", "rule-options"] as const,
    runs: () => ["automation", "runs"] as const,
  },
  campaigns: {
    list: () => ["campaigns"] as const,
    preview: (id: number) => ["campaign", id, "preview"] as const,
    recipients: (id: number) => ["campaign", id, "recipients"] as const,
  },
  documentRequirements: () => ["document-requirements"] as const,
  catalogDestinations: () => ["catalog", "destinations"] as const,
  clientSummary: (clientId: number) => ["client", clientId, "summary"] as const,
  publicReview: (token: string) => ["public-review", token] as const,

  avia: {
    /** The submitted criteria, `null` until the form is submitted. */
    search: <T>(criteria: T) => ["avia", "search", criteria] as const,
  },

  // ------------------------------------------- 09: tur qidiruvi, bronlar, katalog
  hotOffers: {
    all: () => ["hot-offers"] as const,
    list: (query: QueryParams) => ["hot-offers", "list", query] as const,
    summary: () => ["hot-offers", "summary"] as const,
    matches: (offerId: number) => ["hot-offers", offerId, "matches"] as const,
  },
  hotelSearch: <T>(criteria: T) => ["hotel-search", criteria] as const,
  catalogAirports: () => ["catalog", "airports"] as const,
  selection: {
    all: () => ["selection"] as const,
    forOwner: (owner: QueryParams) => ["selection", "owner", owner] as const,
  },
  ownPackages: {
    all: () => ["own-packages"] as const,
    list: (status: string, q: string) => ["own-packages", "list", status, q] as const,
  },
  operatorBookings: {
    all: () => ["operator-bookings"] as const,
    connections: () => ["operator-bookings", "connections"] as const,
    list: (filters: QueryParams) => ["operator-bookings", "list", filters] as const,
    alerts: () => ["operator-bookings", "alerts"] as const,
    messages: (bookingId: number, topic: string) => ["operator-bookings", bookingId, "messages", topic] as const,
  },
  catalogMapping: {
    all: () => ["catalog-mapping"] as const,
    list: (query: QueryParams) => ["catalog-mapping", "list", query] as const,
    summary: () => ["catalog-mapping", "summary"] as const,
  },
  supplierConnections: {
    all: () => ["supplier-connections"] as const,
    providers: () => ["supplier-connections", "providers"] as const,
    credentials: () => ["supplier-connections", "credentials"] as const,
  },
} as const;
